/**
 * Settings ▸ Test Pricing ▸ Default Pricing ▸ New — the pure parts of the G5
 * "New" list (TestPriceSurcharge_ListView_newlist, TestPriceViewController):
 * row shape, the client-side checks and the Save payload. The server repeats
 * every check (DefaultPricingNewRules); these only stop a doomed round trip.
 */

export const DPN_PAGE_ID = 'set-test-default-pricing';
export const DPN_FORM_NAME = 'Default Pricing';
export const DPN_BASE = '/api/v1/default-pricings';

/** G5 messages, verbatim (LDMMessages / TestPriceViewController). */
export const DPN_SELECT_ROW = 'Please select an item to proceed further.';
export const DPN_NO_TAT = 'Select atleast one TAT';
export const DPN_NO_PRICE = 'Please enter the price.';
export const DPN_NEGATIVE = 'Not allowed negative value.';

/** One combination with no price yet, as GET new-candidates returns it. */
export interface DpnCandidate {
  key: string;
  matrixId: string;
  matrix: string | null;
  testId: string;
  test: string | null;
  isGroup: boolean;
  methodId: string | null;
  method: string | null;
  componentId: string | null;
  component: string | null;
  priorityId: string;
  priority: string | null;
  prioritySort: number;
}

/** A grid row: the candidate plus the four cells G5 lets the user type in. */
export interface DpnRow extends DpnCandidate {
  /** TurnAroundTime names (the G5 checked list stores names). */
  tat: string[];
  /** Surcharge (%) as typed; starts at 0, as G5's Surcharge is a non-null int. */
  surcharge: string;
  /** Price as typed. */
  price: string;
  remark: string;
  /** PriceCodeMatch — stored as the price's Invoice Code. */
  priceCodeMatch: string;
}

export const toDpnRow = (c: DpnCandidate): DpnRow => ({ ...c, tat: [], surcharge: '0', price: '', remark: '', priceCodeMatch: '' });

/** A row the user has typed into (the unsaved-changes guard watches these). */
export const isDpnEdited = (r: DpnRow): boolean =>
  r.tat.length > 0 || (r.surcharge.trim() !== '' && r.surcharge.trim() !== '0') || r.price.trim() !== '' || r.remark.trim() !== '' || r.priceCodeMatch.trim() !== '';

const num = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/**
 * The first G5 problem in the ticked rows, or null. G5 checks the non-group rows
 * first, then the group rows: each needs a TAT; a non-group row also a non-zero
 * price; a negative Surcharge is refused.
 */
export function firstDpnProblem(rows: DpnRow[]): string | null {
  if (rows.length === 0) return DPN_SELECT_ROW;
  for (const r of [...rows.filter((x) => !x.isGroup), ...rows.filter((x) => x.isGroup)]) {
    const s = num(r.surcharge);
    if (s != null && s < 0) return DPN_NEGATIVE;
    if (r.tat.length === 0) return DPN_NO_TAT;
    if (!r.isGroup && !num(r.price)) return DPN_NO_PRICE;
  }
  return null;
}

/** The Save payload for one ticked row (POST save-new). */
export const toDpnInput = (r: DpnRow) => ({
  matrix: r.matrixId,
  test: r.testId,
  isGroup: r.isGroup,
  method: r.isGroup ? null : r.methodId,
  component: r.isGroup ? null : r.componentId,
  priority: r.priorityId,
  tat: r.tat,
  surcharge: num(r.surcharge) == null ? 0 : Math.trunc(num(r.surcharge)!),
  surchargePrice: num(r.price),
  remark: r.remark.trim() === '' ? null : r.remark,
  invoiceCode: r.priceCodeMatch.trim() === '' ? null : r.priceCodeMatch.trim(),
});

/** The search box: matches any shown cell. */
export function filterDpnRows(rows: DpnRow[], search: string): DpnRow[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    [r.matrix, r.test, r.method, r.component, r.priority, r.tat.join('; '), r.surcharge, r.price, r.remark, r.priceCodeMatch, String(r.prioritySort)]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
}
